//* Libraries imports
import { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Container, Row, Col } from "react-bootstrap";
import "./content.scss";

//* Hooks imports
import { useGetEssayById } from "../../../../hooks/queries/essay/use-get-essay";
import { useGetProcessById } from "../../../../hooks/queries/process/use-get-process";

//* Components imports
import { EssayViewer } from "./essay-viewer";
import { EssayFeedback } from "./essay-feedback";
import { CriteriosZeroAlert } from "./criterios-zero-alert";
import { ErrorMessages } from "../../../../component/common/ErrorMessages";
import { ViewHeaderProcess } from "../../../ProcessView/view/_components/view-header-process";
import { ProcessStatus } from "../../../../component/common/ProcessStatus";
import { EssayViewSkeleton } from "../../../../skeleton/EssayViewSkeleton";

export function Content() {
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);

  const { id, essayId } = useParams<{ id: string; essayId: string }>();
  const navigate = useNavigate();

  const idNumber = useMemo(() => {
    if (!id) return null;
    const parsed = parseInt(id, 10);
    return isNaN(parsed) ? null : parsed;
  }, [id]);

  const essayIdNumber = useMemo(() => {
    if (!essayId) return null;
    const parsed = parseInt(essayId, 10);
    return isNaN(parsed) ? null : parsed;
  }, [essayId]);

  const processId = idNumber || 0;

  // Get process data for header
  const processQuery = useGetProcessById(processId);
  const {
    isLoading: processLoading,
    isError: processError,
    data: processResponse,
  } = processQuery;

  // Get essay data
  const {
    data: essayResponse,
    isLoading: essayLoading,
    error: essayError,
  } = useGetEssayById(idNumber, essayIdNumber || 0);

  const essay = useMemo(() => {
    return essayResponse?.data || null;
  }, [essayResponse]);
  const allCriteriosZero = useMemo(() => {
    if (!essay?.corrections) return [];
    return essay.corrections
      .flatMap((correction) => correction.criteriosZero || [])
      .filter((item: any) => item.value == true)
      .filter(
        (criterio): criterio is { id: number; name: string } =>
          criterio !== undefined && criterio !== null
      );
  }, [essay]);

  const allAnnotations = useMemo(() => {
    if (!essay?.corrections) return [];
    return essay.corrections.flatMap(
      (correction) =>
        correction.annotations?.map((annotation, index) => ({
          ...annotation,
          correctionId: correction.id,
          displayIndex: index + 1,
        })) || []
    );
  }, [essay]);

  const mappedEssay = useMemo(() => {
    if (!essay) return null;
    return {
      title: essay.name,
      prompt: essay.enunciado,
      candidateTitle: essay.titleCandidate,
      ...essay,
    };
  }, [essay]);

  const hasCriteriosZero = allCriteriosZero.length > 0;

  if (!processId || !essayIdNumber) {
    navigate(-1);
    return null;
  }
  const handleBack = () => {
    navigate(`/process/${processId}/essay`);
  };

  const handleCloseSidebar = () => {
    setIsSidebarVisible(false);
  };

  const handleOpenSidebar = () => {
    setIsSidebarVisible(true);
  };
  const renderMainContent = () => {
    const process = processResponse!.data!;

    if (!process.isFinished) {
      return (
        <div className="d-flex justify-content-center align-items-center min-vh-100 w-100">
          <ProcessStatus
            type="in-progress"
            title="Processo em Andamento"
            subtitle={process.name}
            message="Este processo seletivo ainda não foi finalizado. As redações estarão disponíveis para visualização assim que o processo for concluído."
            actionLabel="Voltar ao Processo"
            onAction={handleBack}
          />
        </div>
      );
    }

    return (
      <motion.div
        className="essay-view-container min-vh-100 bg-light"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
      >
        <div className="d-flex flex-column h-100 w-100">
          <ViewHeaderProcess
            processName={process?.name}
            handleBack={handleBack}
            isOnline={process?.online}
            startDate={process?.start?.human?.date}
            endDate={process?.end?.human?.date}
            collegeName={process?.college?.name}
          />

          <Container fluid className="flex-grow-1 py-4" style={{padding:10}}>
            {/* Criterios Zero Section - Above main content */}
            {hasCriteriosZero && (
              <Container className="mb-4">
                <Row className="justify-content-center">
                  <Col xxl={10} xl={11} lg={12} style={{width:'100%'}}>
                    <CriteriosZeroAlert criteriosZero={allCriteriosZero} />
                  </Col>
                </Row>
              </Container>
            )}
            <Row className="justify-content-center position-relative">
              <motion.div
                layout
                transition={{
                  duration: 0.5,
                  ease: [0.4, 0, 0.2, 1],
                }}
                className="col-12"
                // className={`col-xxl-${isSidebarVisible && allAnnotations.length > 0 ? '8' : '11'} col-xl-${isSidebarVisible && allAnnotations.length > 0 ? '8' : '11'} col-lg-${isSidebarVisible && allAnnotations.length > 0 ? '7' : '11'} col-md-${isSidebarVisible && allAnnotations.length > 0 ? '7' : '12'} col-sm-12 essay-viewer-col pe-0 ${hasCriteriosZero ? 'has-criterios-zero' : ''}`}
              >
                <EssayViewer
                  essay={mappedEssay!}
                  hasCriteriosZero={hasCriteriosZero}
                />
              </motion.div>

              {/* {allAnnotations.length > 0 && (
                <motion.div
                  layout
                  initial={false}
                  animate={{
                    opacity: isSidebarVisible ? 1 : 0,
                    x: isSidebarVisible ? 0 : 300,
                    pointerEvents: isSidebarVisible ? 'auto' : 'none'
                  }}
                  transition={{ 
                    duration: 0.5, 
                    ease: [0.4, 0, 0.2, 1],
                    opacity: { duration: 0.3 }
                  }}
                  className="col-xxl-4 col-xl-4 col-lg-5 col-md-5 col-sm-12 essay-feedback-col ps-3 pt-4"
                  style={{
                    position: isSidebarVisible ? 'relative' : 'absolute',
                    right: isSidebarVisible ? 'auto' : '-100%',
                    zIndex: 10
                  }}
                >
                  <EssayFeedback 
                    corrections={essay!.corrections} 
                    isVisible={isSidebarVisible}
                    onClose={handleCloseSidebar}
                  />
                </motion.div>
              )} */}
              {/* Floating button to reopen sidebar when closed */}
              {allAnnotations.length > 0 && !isSidebarVisible && (
                <motion.button
                  className="floating-feedback-btn"
                  onClick={handleOpenSidebar}
                  initial={{ opacity: 0, scale: 0, y: 100 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0, y: 100 }}
                  transition={{
                    duration: 0.4,
                    delay: 0.3,
                    ease: [0.4, 0, 0.2, 1],
                  }}
                  whileHover={{
                    scale: 1.1,
                    y: -5,
                    transition: { duration: 0.2 },
                  }}
                  whileTap={{
                    scale: 0.95,
                    transition: { duration: 0.1 },
                  }}
                  aria-label="Abrir sidebar de feedback"
                  title="Ver feedbacks"
                >
                  <motion.svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    initial={{ scale: 0.8, rotate: -10 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ duration: 0.3, delay: 0.5 }}
                    whileHover={{ scale: 1.1, rotate: 5 }}
                  >
                    <path d="M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h4l4 4 4-4h4c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z" />
                  </motion.svg>
                  <motion.span
                    className="feedback-count"
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{
                      duration: 0.4,
                      delay: 0.7,
                      type: "spring",
                      stiffness: 300,
                      damping: 20,
                    }}
                  >
                    {allAnnotations.length}
                  </motion.span>
                </motion.button>
              )}
            </Row>
          </Container>
        </div>
      </motion.div>
    );
  };

  if (processLoading || essayLoading) {
    const hasAnnotations = essay?.corrections?.some(
      (correction) =>
        correction.annotations && correction.annotations.length > 0
    );
    const shouldShowSidebar = hasAnnotations || essayLoading;

    return <EssayViewSkeleton showSidebar={shouldShowSidebar} />;
  }

  if (!essayIdNumber) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <ErrorMessages
          messages={[
            "ID da redação inválido. Verifique o link e tente novamente.",
          ]}
          clearErrors={handleBack}
        />
      </div>
    );
  }

  if (
    processError ||
    !processResponse?.success ||
    !processResponse?.data ||
    essayError ||
    !essay
  ) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <ErrorMessages
          messages={[
            processResponse?.message ||
              essayResponse?.message ||
              "Não foi possível carregar a redação. Tente novamente.",
          ]}
          clearErrors={handleBack}
        />
      </div>
    );
  }

  return renderMainContent();
}
